import React, {useEffect, useMemo, useState} from 'react';
import {BrowserRouter as Router, Route, Routes, useLocation} from 'react-router-dom';
import {useForm, SubmitHandler} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import Input from "./Input";
import {useTariff} from "../../context/TariffContext";
import {useModal} from "../../context/ModalContext";
import {useResponseStatus} from "../../context/ResponseStatusContext";
import axios from "axios";
import styles from "./Form.module.scss";
import cn from "classnames";
import Button from "../Button/Button";
import {Row, Col} from "react-bootstrap";
import {IFormValuesMerchant} from "../../types/formTypes";
import SelectSearch from "./SelectSearch";
import regionsData from "../../data/regionsData.json";
import docsData from "../../data/docsData.json";

const dataSchema = yup.object().shape({
    surname: yup.string().required("Фамилия обязательное поле"),
    firstName: yup.string().required("Имя обязательное поле"),
    patronymic: yup.string().required("Отчество обязательное поле"),
    phoneNumber: yup.string().required("Номер телефона обязательное поле")
        .matches(/^\+7 \(\d{3}\) \d{3}-\d{2}-\d{2}$/, "Некорректный формат номера"),
    email: yup.string().required("Email обязательное поле").email("Некорректный формат email"),
    tariff: yup.string().required("Необходимо выбрать тариф"),
    externalCode: yup.number(),
    // promoCode: yup.string(),
    addressRegistration: yup.string(),
    countryId: yup.string(),
});

interface FormProps {
    closeModal?: () => void;
    className?: string;
}

const FormMerchant: React.FC<FormProps> = ({closeModal, className}) => {
    const {register, handleSubmit, setValue, formState: {errors}} = useForm<IFormValuesMerchant>({
        resolver: yupResolver(dataSchema),
    });

    // Получаем partner_id из URL
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const partnerId = searchParams.get('partner_id');

    const [isSubmitDisabled, setIsSubmitDisabled] = useState(true); // Состояние для кнопки submit
    const [status, setStatus] = useState<'pending' | 'error' | 'success' | 'initial'>('initial');

    // Устанавливаем значение externalCode при монтировании
    React.useEffect(() => {
        if (partnerId) {
            const parsedPartnerId = Number(partnerId); // Преобразуем в число
            if (!isNaN(parsedPartnerId)) {
                setValue('externalCode', parsedPartnerId);
                setIsSubmitDisabled(false); // Если partner_id есть, делаем кнопку доступной
            }
        } else {
            setIsSubmitDisabled(true); // Если нет partner_id, делаем кнопку заблокированной
        }
    }, [partnerId, setValue]);

    const {openModalC} = useModal();
    const {selectedTariffId} = useTariff();
    const {setResponseStatus, setFormType} = useResponseStatus();

    const onSubmit: SubmitHandler<IFormValuesMerchant> = async (data) => {
        setStatus('pending'); // Начинаем отправку формы
        setFormType('payment');

        // console.log("Form data on submit:", data);
        // if (!data.addressRegistration) {
        //     console.log("addressRegistration is missing!");
        // }

        const formData = new FormData();
        Object.entries(data).forEach(([key, value]) => {
            formData.append(key, value);
        });

        try {
            const response = await axios.post("./php/payInit.php", formData, {
                headers: {
                    'X-Requested-With': 'XMLHttpRequest',
                },
            });

            if (response.data.status === "success" && response.data.paymentUrl) {
                setStatus('success');
                // Редирект на paymentUrl
                window.location.href = response.data.paymentUrl;
            } else {
                setStatus('error'); // Ошибка
                // console.error("Ошибка:", response.data.message || "Произошла ошибка.");
                setResponseStatus(false);
                setTimeout(openModalC, 200);
            }
            closeModal?.();
        } catch (error) {
            setStatus('error'); // Ошибка
            // console.error("Ошибка:", error);
            setResponseStatus(false);
            setTimeout(openModalC, 200);
            closeModal?.();
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}
              className={cn(styles.form, className ? styles[className] : undefined)}
        >
            <Row className="g-4 align-items-end">
                <Col sm={6} md={12}>
                    <Input label="Фамилия*"
                           name="surname"
                           placeholder="Иванов"
                           register={register}
                           required
                           className={className}
                           errorMessage={errors.surname?.message}
                    />
                </Col>
                <Col sm={6} md={6}>
                    <Input label="Имя*"
                           name="firstName"
                           placeholder="Иван"
                           register={register}
                           required
                           className={className}
                           errorMessage={errors.firstName?.message}
                    />
                </Col>
                <Col sm={6} md={6}>
                    <Input label="Отчество*"
                           name="patronymic"
                           placeholder="Иванович"
                           register={register}
                           required
                           className={className}
                           errorMessage={errors.patronymic?.message}
                    />
                </Col>
                <Col sm={6} md={6}>
                    <Input label="Телефон*"
                           name="phoneNumber"
                           placeholder="+7 (900) 000-00-00"
                           register={register}
                           required
                           className={className}
                           mask="+7 (999) 999-99-99"
                           errorMessage={errors.phoneNumber?.message}
                    />
                </Col>
                <Col sm={6} md={6}>
                    <Input label="E-mail*"
                           name="email"
                           placeholder="example@gmail.com"
                           register={register}
                           required
                           className={className}
                           errorMessage={errors.email?.message}
                    />
                </Col>
                {/*<Col sm={6} md={12}>*/}
                {/*    <Input label="Промокод"*/}
                {/*           name="promoCode"*/}
                {/*           placeholder="Введите промокод"*/}
                {/*           register={register}*/}
                {/*           className={className}*/}
                {/*    />*/}
                {/*</Col>*/}
                <Col sm={6} md={12}>
                    <SelectSearch
                        label="Выберите регион"
                        name="addressRegistration"
                        register={register}
                        className={className}
                        options={regionsData.map(region => ({
                            value: region.label,
                            label: region.label,
                            id: region.id
                        }))}
                        setValue={setValue}
                    />
                </Col>
                <Col xs={12} sm={8}>
                    <Button type="submit"
                            classButton="redButton"
                            className="w-100"
                            disabled={status === 'pending' || isSubmitDisabled}
                    >
                        {status === 'pending' ? 'Отправка...' : 'Оплатить картой'}
                    </Button>
                </Col>
                <Col xs={12} sm={4}>
                    <Button onClick={closeModal} className="w-100">
                        Назад
                    </Button>
                </Col>
                <Col xs={12}>
                    Нажимая «Оплатить картой»,  я соглашаюсь <a
                    href={`${window.location.origin}/${docsData[2].link.replace(/^\.?\//, '')}`} target="_blank">
                    {docsData[2].title.toLowerCase()}
                </a>
                    , а также что ознакомлен с <a href={`${window.location.origin}/${docsData[1].link.replace(/^\.?\//, '')}`}
                                                  target="_blank">{docsData[1].title.toLowerCase()}
                </a>
                </Col>
            </Row>

            <Input name="tariff"
                   register={register}
                   className={className}
                   required
                   value={selectedTariffId || ""}
                   type="hidden"
                   errorMessage={errors.tariff?.message}
            />
            <Input name="externalCode"
                   register={register}
                   type="hidden"
            />
            <Input name="countryId"
                   register={register}
                   type="hidden"
            />
        </form>
    );
};

export default FormMerchant;
